import { MainContents } from '@/components/MainContents';
import { useFetchCandidateImportRequest } from '../api/useFetchCandidateImportRequest';
import { useParams } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { ImportLoadingView } from '../components/ImportLoadingView';
import { CandidateImportMappingView } from '../components/CandidateImportMappingView';
import { CandidateImportConfirmView } from '../components/CandidateImportConfirmView';
import { CandidateImportPresetSaveView } from '../components/CandidateImportPresetSaveView';

export function CandidateImportRequestsDetailTemplate() {
  const { encryptId } = useParams({ strict: false }) as { encryptId: string };
  const [isImporting, setIsImporting] = useState(false);
  const [showPresetSave, setShowPresetSave] = useState(false);
  const [savedFieldMappings, setSavedFieldMappings] = useState<Record<number, number[]>>({});
  const [savedDefaultMappings, setSavedDefaultMappings] = useState<Record<number, string>>({});
  const [savedOrganizationUserId, setSavedOrganizationUserId] = useState<number | null>(null);
  const [savedNameCsvIndices, setSavedNameCsvIndices] = useState<number[]>([0]);
  const [savedNameDefaultValue, setSavedNameDefaultValue] = useState<string>('');
  const [savedPresetTitle, setSavedPresetTitle] = useState<string>('');
  const [showCompletionAnimation, setShowCompletionAnimation] = useState(false);

  // 取り込み中はポーリング（2秒ごと）、それ以外はポーリング無効
  const { data: importRequest, isLoading } = useFetchCandidateImportRequest(encryptId, {
    refetchInterval: isImporting ? 2000 : false,
  });

  // pre_built になったら100%表示してから画面遷移
  useEffect(() => {
    if (isImporting && importRequest?.status === 'pre_built') {
      // 100%表示のアニメーションを開始
      setShowCompletionAnimation(true);
      // 1秒後に画面遷移
      setTimeout(() => {
        setIsImporting(false);
        setShowCompletionAnimation(false);
      }, 1000);
    }
  }, [isImporting, importRequest?.status]);

  if (isLoading) {
    return (
      <MainContents>
        <div className="flex items-center justify-center p-lg">
          <p className="text-body-md">読み込み中...</p>
        </div>
      </MainContents>
    );
  }

  if (!importRequest) {
    return (
      <MainContents>
        <div className="flex items-center justify-center p-lg">
          <p className="text-body-md">データが見つかりません</p>
        </div>
      </MainContents>
    );
  }

  const handleMappingSubmitSuccess = () => {
    // 取り込み開始：ローディング表示＆ポーリング開始
    setIsImporting(true);
  };

  const handleShowPresetSave = (
    fieldMappings: Record<number, number[]>,
    defaultMappings: Record<number, string>,
    selectedOrganizationUserId: number | null,
    nameCsvIndices: number[],
    nameDefaultValue: string,
    presetTitle?: string
  ) => {
    setSavedFieldMappings(fieldMappings);
    setSavedDefaultMappings(defaultMappings);
    setSavedOrganizationUserId(selectedOrganizationUserId);
    setSavedNameCsvIndices(nameCsvIndices);
    setSavedNameDefaultValue(nameDefaultValue);
    setSavedPresetTitle(presetTitle || '');
    setShowPresetSave(true);
  };

  const handlePresetSaveSuccess = () => {
    // プリセット保存成功後、取り込み開始：ローディング表示＆ポーリング開始
    setShowPresetSave(false);
    setIsImporting(true);
  };

  const handleBackFromPresetSave = () => {
    setShowPresetSave(false);
  };

  // 取り込み中（confirmed ステータス）の場合はローディング表示
  // pre_built になったら100%表示してから確認画面に遷移
  if ((isImporting && importRequest?.status !== 'pre_built') || showCompletionAnimation) {
    return (
      <MainContents>
        <ImportLoadingView progress={showCompletionAnimation ? 100 : undefined} />
      </MainContents>
    );
  }

  // プリセット保存画面
  if (showPresetSave) {
    return (
      <CandidateImportPresetSaveView
        importRequest={importRequest}
        encryptId={encryptId}
        fieldMappings={savedFieldMappings}
        defaultMappings={savedDefaultMappings}
        selectedOrganizationUserId={savedOrganizationUserId}
        nameCsvIndices={savedNameCsvIndices}
        nameDefaultValue={savedNameDefaultValue}
        initialTitle={savedPresetTitle}
        onSaveSuccess={handlePresetSaveSuccess}
        onBack={handleBackFromPresetSave}
      />
    );
  }

  // pre_built ステータス: 重複チェック画面
  if (importRequest.status === 'pre_built') {
    return (
      <MainContents>
        <CandidateImportConfirmView importRequest={importRequest} encryptId={encryptId} />
      </MainContents>
    );
  }

  // draft ステータス: データ割り当て画面（デフォルト）
  return (
    <MainContents>
      <CandidateImportMappingView
        importRequest={importRequest}
        encryptId={encryptId}
        onSubmitSuccess={handleMappingSubmitSuccess}
        onShowPresetSave={handleShowPresetSave}
      />
    </MainContents>
  );
}
